/**
 * Foto's bij feedback: kiezen, plakken, en tonen (Tom, 15-09-2026: "Zorg ook dat
 * die foto's kan uploaden").
 *
 * Gedeeld door de feedbackknop en de ticketpagina. Plakken met Ctrl+V werkt in
 * het tekstveld: een schermafbeelding maken en meteen plakken is de snelste weg.
 * Welke bestanden echt een afbeelding zijn en hoe groot ze mogen zijn, beslist
 * de API; hier alleen wat de browser toont.
 */
import { useQuery } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useEffect, useMemo } from "react";

import { type FeedbackAttachment, fetchFeedbackAttachment } from "@/api/feedback";

import { FOTO_SOORTEN } from "./fotos";
import "./Bijlagen.css";

/** Het voorbeeld van een nog niet verstuurde foto. */
function Voorbeeld({ bestand, onWeg }: { bestand: File; onWeg: () => void }) {
  const url = useMemo(() => URL.createObjectURL(bestand), [bestand]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <span className="atl-bijlage atl-bijlage--nieuw">
      <img src={url} alt={bestand.name} />
      <button type="button" onClick={onWeg} aria-label={`${bestand.name} weghalen`}>
        <X size={12} aria-hidden />
      </button>
    </span>
  );
}

/** Foto's kiezen, met voorbeelden van wat er straks meegaat. */
export function FotoKiezer({
  id, bestanden, onChange,
}: { id: string; bestanden: File[]; onChange: (bestanden: File[]) => void }) {
  return (
    <div className="atl-fotokiezer">
      <label htmlFor={id} className="atl-fotokiezer__knop">
        <Plus size={14} strokeWidth={2} aria-hidden /> Foto toevoegen
      </label>
      <input
        id={id}
        type="file"
        accept={FOTO_SOORTEN}
        multiple
        className="atl-sr-only"
        onChange={(event) => {
          onChange([...bestanden, ...Array.from(event.target.files ?? [])]);
          event.target.value = "";
        }}
      />
      <span className="atl-fotokiezer__hint">of plak een schermafbeelding</span>
      {bestanden.length > 0 && (
        <div className="atl-bijlagen">
          {bestanden.map((bestand, index) => (
            <Voorbeeld
              key={`${bestand.name}-${index}`}
              bestand={bestand}
              onWeg={() => onChange(bestanden.filter((_, i) => i !== index))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Een opgeslagen foto: opgehaald met het aanmeldtoken en getoond via een object-URL. */
function Foto({ ticket, bijlage }: { ticket: string; bijlage: FeedbackAttachment }) {
  const query = useQuery({
    queryKey: ["feedback-bijlage", ticket, bijlage.id],
    queryFn: () => fetchFeedbackAttachment(ticket, bijlage.id),
    staleTime: Infinity,
  });
  const url = useMemo(() => (query.data ? URL.createObjectURL(query.data) : null), [query.data]);
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  if (query.isError) {
    return <span className="atl-bijlage atl-bijlage--fout">{bijlage.filename} is niet op te halen</span>;
  }
  if (!url) {
    return <span className="atl-bijlage atl-bijlage--laden" aria-label={`${bijlage.filename} wordt geladen`} />;
  }
  return (
    <a className="atl-bijlage" href={url} target="_blank" rel="noopener noreferrer" title={bijlage.filename}>
      <img src={url} alt={bijlage.filename} />
    </a>
  );
}

export function FotoRij({ ticket, bijlagen }: { ticket: string; bijlagen: FeedbackAttachment[] }) {
  if (bijlagen.length === 0) return null;
  return (
    <div className="atl-bijlagen">
      {bijlagen.map((bijlage) => <Foto key={bijlage.id} ticket={ticket} bijlage={bijlage} />)}
    </div>
  );
}
